import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { Avatar } from '../components_catalyst/avatar';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../components_catalyst/dropdown';
import { Navbar, NavbarItem, NavbarSection, NavbarSpacer } from '../components_catalyst/navbar';
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from '../components_catalyst/sidebar';
import { SidebarLayout } from '../components_catalyst/sidebar-layout';
import {
  ArrowRightStartOnRectangleIcon,
  BookOpenIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Cog8ToothIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  UserGroupIcon,
  QuestionMarkCircleIcon,
  UserIcon,
  BellIcon,
  BookmarkIcon,
  UsersIcon,
} from '@heroicons/react/16/solid';
import { MapIcon, MagnifyingGlassIcon, SparklesIcon } from '@heroicons/react/20/solid';
import { EnvironmentReminder } from '../utils/EnvironmentReminder';
import { useAuth } from '../components_auth/AuthContext';
import { UserbackWidget, openUserbackWidget } from '../components_feedback';
import { AppDispatch } from '../store/store';
// import { getInitialsFromNames } from '../utils/avatarUtils';
import { getInitials } from '../utils/avatarUtils';

export function AuthenticatedSidebarLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);
  const userName = userProfile.userName;

  // Generate consistent initials from user profile
  const userInitials = getInitials(userName);

  const recentlyAccessedMaps = [...(userProfile.accessedMilestoneMaps || [])]
    .sort((a, b) => {
      // Sort pinned items first
      // TODO: This is not used in the UI as of 2/10/25. If it doesn't get added back in soon,
      // we should remove this (and remove this propoerty from User1 type).
      // if (a.pinned && !b.pinned) return -1;
      // if (!a.pinned && b.pinned) return 1;

      // Sort by lastAccessed date (most recent first)
      return (b.lastAccessed || '') > (a.lastAccessed || '') ? 1 : -1;
    })
    .slice(0, 5);

  if (!user) {
    return <Navigate to="/sign-in" replace />;
  }

  return (
    <>
      <SidebarLayout
        navbar={
          <Navbar role="banner" aria-label="Top navigation">
            <NavbarSpacer />
            <NavbarSection>
              <Dropdown>
                <DropdownButton as={NavbarItem} aria-label="User menu">
                  <Avatar
                    initials={userInitials}
                    className="bg-slate-500 text-white"
                    aria-hidden="true"
                  />
                </DropdownButton>
                <DropdownMenu className="min-w-64" anchor="bottom end">
                  <DropdownItem href={`/${userName}`}>
                    <UserIcon aria-hidden="true" />
                    <DropdownLabel>My profile</DropdownLabel>
                  </DropdownItem>
                  <DropdownItem href="/settings">
                    <Cog8ToothIcon aria-hidden="true" />
                    <DropdownLabel>Settings</DropdownLabel>
                  </DropdownItem>
                  <DropdownDivider />
                  <DropdownItem href="/privacy-policy">
                    <ShieldCheckIcon aria-hidden="true" />
                    <DropdownLabel>Privacy policy</DropdownLabel>
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => openUserbackWidget()}
                    aria-label="Open feedback form"
                  >
                    <LightBulbIcon aria-hidden="true" />
                    <DropdownLabel>Feedback</DropdownLabel>
                  </DropdownItem>
                  <DropdownDivider />
                  <DropdownItem href="/sign-out">
                    <ArrowRightStartOnRectangleIcon aria-hidden="true" />
                    <DropdownLabel>Sign out</DropdownLabel>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </NavbarSection>
          </Navbar>
        }
        sidebar={
          <Sidebar role="navigation" aria-label="Main navigation">
            <SidebarHeader>
              <SidebarItem
                className="mb-2.5"
                href={`/${userName}`}
                current={location.pathname === `/${userName}`}
                aria-current={location.pathname === `/${userName}` ? 'page' : undefined}
              >
                <Avatar
                  initials={userInitials}
                  className="bg-slate-500 text-white"
                  aria-hidden="true"
                />
                <SidebarLabel>
                  {`${userProfile.firstName || ''} ${userProfile.lastName || ''}`}
                </SidebarLabel>
              </SidebarItem>
            </SidebarHeader>
            <SidebarBody>
              <SidebarSection aria-label="Milestone Maps">
                <SidebarHeading id="maps-heading">Milestone Maps</SidebarHeading>
                <div role="group" aria-labelledby="maps-heading">
                  <SidebarItem
                    href={`/${userName}/maps`}
                    current={location.pathname === `/${userName}/maps`}
                    aria-current={location.pathname === `/${userName}/maps` ? 'page' : undefined}
                  >
                    <MapIcon aria-hidden="true" />
                    <SidebarLabel>{`My Maps`}</SidebarLabel>
                  </SidebarItem>
                  {/*
                  // TODO: Revisit "bookmark" concept and decide if/how to proceed.
                  // Bookmark maps I can access, but that were not "shared" with me?
                  // Bookmark other content of interest, e.g. specific status cell, comment, result item, etc.?
                  <SidebarItem href={`/${userName}/bookmarks`}>
                    <BookmarkIcon />
                    <SidebarLabel>My Bookmarks</SidebarLabel>
                  </SidebarItem>
                  */}
                  <SidebarItem
                    href={`/${userName}/shared`}
                    current={location.pathname === `/${userName}/shared`}
                    aria-current={location.pathname === `/${userName}/shared` ? 'page' : undefined}
                  >
                    <UsersIcon aria-hidden="true" />
                    <SidebarLabel>Shared</SidebarLabel>
                  </SidebarItem>
                </div>
              </SidebarSection>
              {/*
              <SidebarSection>
                <SidebarHeading>Needs Heading Name</SidebarHeading>
                <SidebarItem href="/upcoming">
                  <CalendarIcon />
                  <SidebarLabel>Upcoming</SidebarLabel>
                </SidebarItem>
                <SidebarItem href={`/${userName}/follows`}>
                  <UserGroupIcon />
                  <SidebarLabel>Following</SidebarLabel>
                </SidebarItem>
                <SidebarItem href={`/${userName}/maps/stats`}>
                  <BookOpenIcon />
                  <SidebarLabel>Stats</SidebarLabel>
                </SidebarItem>
              </SidebarSection>
              */}

              <SidebarSection aria-label="Recent maps">
                <SidebarHeading id="recent-heading">Recent</SidebarHeading>
                <div role="group" aria-labelledby="recent-heading">
                  {recentlyAccessedMaps.map((map) => (
                    <div key={map.uuid} className="group relative">
                      <SidebarItem
                        href={`/${map.ownerName}/maps/${map.shortId}`}
                        current={location.pathname === `/${map.ownerName}/maps/${map.shortId}`}
                        aria-current={
                          location.pathname === `/${map.ownerName}/maps/${map.shortId}`
                            ? 'page'
                            : undefined
                        }
                        aria-label={`Open ${map.title} milestone map`}
                      >
                        {map.title}
                      </SidebarItem>
                      {/* TODO: Having pins, bookmarks, and stars feels like too much.
                      // Also if the sidebar is going to have icons for showing privacy levels,
                      // we should reserve that side space for that and not increase clutter with this.
                      // Leaving this here for now, in case we go a different route and want to add it back in.
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        <AddEditDeleteBar1
                          showPin={true}
                          showAdd={false}
                          showEdit={false}
                          showDelete={false}
                          isPinned={map.pinned}
                          isVisible={map.pinned || false}
                          transparent={true}
                          className="group-hover:opacity-100"
                          onPin={() => {
                            const updatedMaps = userProfile.accessedMilestoneMaps?.map((m) =>
                          isVisible={map.pinned || false}
                          transparent={true}
                          className="group-hover:opacity-100"
                          onPin={() => {
                            const updatedMaps = userProfile.accessedMilestoneMaps?.map((m) =>
                              m.uuid === map.uuid ? { ...m, pinned: !m.pinned } : m,
                            );

                            dispatch(
                              updateUser({
                                uid: userProfile.uid,
                                updates: {
                                  accessedMilestoneMaps: updatedMaps,
                                },
                              }),
                            );
                          }}
                        />
                      </div>
                      */}
                    </div>
                  ))}
                </div>
              </SidebarSection>
              <SidebarSpacer />
              <SidebarSection aria-label="Additional links">
                <div role="group">
                  <SidebarItem
                    href={`/${userName}/notifications`}
                    current={location.pathname === `/${userName}/notifications`}
                    aria-current={
                      location.pathname === `/${userName}/notifications` ? 'page' : undefined
                    }
                  >
                    <BellIcon aria-hidden="true" />
                    <SidebarLabel>Notifications</SidebarLabel>
                  </SidebarItem>
                  <SidebarItem
                    href="/support"
                    current={location.pathname === '/support'}
                    aria-current={location.pathname === '/support' ? 'page' : undefined}
                  >
                    <QuestionMarkCircleIcon aria-hidden="true" />
                    <SidebarLabel>Support</SidebarLabel>
                  </SidebarItem>
                  {/*
                  // TODO: Maybe not MVP. Maybe revise as org-specific feature. TBD.
                  <SidebarItem href="/support2">
                    <UserCircleIcon />
                    <SidebarLabel>Support 2.0</SidebarLabel>
                  </SidebarItem>
                  */}
                  <SidebarItem
                    href="/changelog"
                    current={location.pathname === '/changelog'}
                    aria-current={location.pathname === '/changelog' ? 'page' : undefined}
                  >
                    <SparklesIcon aria-hidden="true" />
                    <SidebarLabel>Changelog</SidebarLabel>
                  </SidebarItem>
                </div>
              </SidebarSection>
            </SidebarBody>
            <SidebarFooter>
              <Dropdown>
                <DropdownButton as={SidebarItem} aria-label="User account menu">
                  <span className="flex min-w-0 items-center gap-3">
                    <Avatar
                      initials={userInitials}
                      className="size-10 bg-slate-500 text-white"
                      aria-hidden="true"
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">
                        {`${userProfile.firstName || ''} ${userProfile.lastName || ''}`}
                      </span>
                      <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">
                        {userName}
                      </span>
                    </span>
                  </span>
                  <ChevronUpIcon aria-hidden="true" />
                </DropdownButton>
                <DropdownMenu className="min-w-64" anchor="top start">
                  <DropdownItem href={`/${userName}`}>
                    <UserIcon aria-hidden="true" />
                    <DropdownLabel>My profile</DropdownLabel>
                  </DropdownItem>
                  <DropdownItem href={`/${userName}/settings`}>
                    <Cog8ToothIcon aria-hidden="true" />
                    <DropdownLabel>Settings</DropdownLabel>
                  </DropdownItem>
                  <DropdownDivider />
                  <DropdownItem href="/privacy-policy">
                    <ShieldCheckIcon aria-hidden="true" />
                    <DropdownLabel>Privacy policy</DropdownLabel>
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => openUserbackWidget()}
                    aria-label="Open feedback form"
                  >
                    <LightBulbIcon aria-hidden="true" />
                    <DropdownLabel>Feedback</DropdownLabel>
                  </DropdownItem>
                  <DropdownDivider />
                  <DropdownItem href="/sign-out">
                    <ArrowRightStartOnRectangleIcon aria-hidden="true" />
                    <DropdownLabel>Sign out</DropdownLabel>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </SidebarFooter>
          </Sidebar>
        }
      >
        <EnvironmentReminder />
        <Outlet />
        <UserbackWidget
          userId={user?.uid}
          userEmail={user?.email || undefined}
          userName={`${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim() || undefined}
          metadata={{ page: location.pathname }}
        />
      </SidebarLayout>
    </>
  );
}
